import React, { useEffect, useRef } from 'react';
import PageSeo from '../components/seo/PageSeo';
import { Link, useSearchParams } from 'react-router-dom';
import Pagination from '../components/Pagination';
import NewsListSkeleton from '../components/NewsListSkeleton';
import NewsImage from '../components/NewsImage';
import NewsRowList from '../components/NewsRowList';
import { useSectionNewsQuery } from '../hooks/useNewsListQuery';
import UplClubs from './UplClubs';
import '../styles/SubsectionContent.css';
import ClubInfoCard from '../infocard/ClubInfoCard';
import { ClubInfoMap } from '../infocard/ClubInfoMap';
import FirstLeagueClubs from './FirstLeagueClubs';
import SecondLeagueClubs from './SecondLeagueClubs';
import { getSectionRoute } from '../config/newsSectionRoutes';

const SubsectionContent = ({ section, category }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mountLoggedRef = useRef(false);
  const newsLoggedRef = useRef(false);

  const newsPerPage = 25;
  const currentPage = Number(searchParams.get('page')) || 1;
  const neededLimit = currentPage * newsPerPage;
  const {
    news: sectionNews,
    initialLoading,
    isRefreshing,
    isUsingFallback,
    totalCount,
  } = useSectionNewsQuery(section, neededLimit, currentPage === 1);
  const totalPages = Math.max(1, Math.ceil(totalCount / newsPerPage));

  const handlePageChange = (page) => {
    setSearchParams({ page: String(page) });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const startIndex = (currentPage - 1) * newsPerPage;
  const pageNews = sectionNews.slice(startIndex, startIndex + newsPerPage);

  const heroNews = pageNews[0];
  const restNews = pageNews.slice(1);
  const isClubPage = Boolean(ClubInfoMap[section]);
  const sectionRoute = getSectionRoute(section);
  const canonicalUrl = sectionRoute
    ? `https://bigsport.com.ua${sectionRoute}`
    : null;

  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development'
      && isClubPage
      && !mountLoggedRef.current
    ) {
      mountLoggedRef.current = true;
      console.debug(
        `[CLUB PAGE MOUNT] ${section} ${performance.now().toFixed(1)}ms`
      );
    }
  }, [isClubPage, section]);

  useEffect(() => {
    newsLoggedRef.current = false;
  }, [section]);

  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development'
      && isClubPage
      && sectionNews.length > 0
      && !newsLoggedRef.current
    ) {
      newsLoggedRef.current = true;
      const timestamp = performance.now().toFixed(1);
      console.debug(
        `[CLUB NEWS STATE UPDATED] ${section} ${timestamp}ms`
      );
      if (isUsingFallback) {
        console.debug(
          `[CLUB NEWS FALLBACK] ${section} count=${sectionNews.length} ${timestamp}ms`
        );
      }
      console.debug(
        `[CLUB SKELETON HIDDEN] ${section} ${timestamp}ms`
      );
    }
  }, [
    isClubPage,
    isUsingFallback,
    section,
    sectionNews.length,
  ]);

  return (
    <div className="home-container panel panel--spaced" aria-busy={isRefreshing}>
      {canonicalUrl && (
        <PageSeo
          routePath={sectionRoute}
          imageAlt={`${section} — BigSPORT`}
        />
      )}
      <h1>{section}</h1>

      {section === 'УПЛ' && <UplClubs />}
      {section === 'Перша Ліга' && <FirstLeagueClubs />}
      {section === 'Друга Ліга' && <SecondLeagueClubs />}

      {ClubInfoMap[section] && <ClubInfoCard club={ClubInfoMap[section]} />}

      <div className="news-content-wrapper">
        {initialLoading && sectionNews.length === 0 ? (
          <NewsListSkeleton count={8} />
        ) : (
          <>
          <div className="subsection-news-layout">
            {heroNews && (
              <Link
                to={`/news/${heroNews.id}`}
                className="subsection-hero-news"
              >
                <NewsImage news={heroNews} alt={heroNews.title} loading="eager" />

                <div className="subsection-hero-overlay">
                  <span>
                    {heroNews.day}.{heroNews.month}.{heroNews.year}
                    {heroNews.time ? ` ${heroNews.time}` : ''}
                  </span>

                  <h2>{heroNews.title}</h2>
                </div>
              </Link>
            )}

            <NewsRowList news={restNews} />
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              disabled={isRefreshing}
              sequential
              pageType="section"
              category={category}
              section={section}
            />
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubsectionContent;
